import React, { useEffect, useMemo, useState } from "react";
import { CalendarClock, Plus, Trash2, Layers, AlertCircle } from "lucide-react";
import apiClient from "../../utils/apiClient";
import { AppModal, AppButton } from "../ui";

/* ── date helpers (kept local so the modal is self-contained) ────────────── */
const toISODate = (d) => {
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return "";
  return x.toISOString().slice(0, 10);
};
const addMonths = (date, n) => {
  const d = new Date(date);
  const target = new Date(d.getFullYear(), d.getMonth() + n, d.getDate());
  if (target.getDate() !== d.getDate()) target.setDate(0);
  return target;
};
const rupees = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

/** Split `total` into `n` whole-rupee EMIs (remainder folded into the last). */
function buildEmiRows(total, n, startISO, intervalMonths = 1) {
  const rows = [];
  const base = Math.floor(total / n);
  let allocated = 0;
  const start = startISO ? new Date(startISO) : new Date();
  for (let i = 0; i < n; i++) {
    const isLast = i === n - 1;
    const amount = isLast ? total - allocated : base;
    allocated += amount;
    rows.push({ label: `EMI ${i + 1}`, amount, dueDate: toISODate(addMonths(start, i * intervalMonths)) });
  }
  return rows;
}

/**
 * FeePlanModal — create / edit the installment (EMI) schedule for one enrollment.
 *
 * Props:
 *   show, onHide
 *   courseAssignment — { _id, totalFees, paidFees, courseId?, batchId? }
 *   existingPlan     — optional FeePlan (with installments) to edit
 *   onSaved(plan)    — called after a successful save
 */

/* ── Converted to Tailwind, 2026-08-25 (Phase 5). fee-plan.scss deleted in the
   same commit (TOKEN_CONTRACT §6.1). What the stylesheet was doing:

   🔴 The active segment button was `background: var(--primary-color); color: #fff`.
      `--primary-color` is one of Phase 1.1's ALIASES — scaffolding due for
      deletion in 5.2 — and the `#fff` is the Phase 7.1 mistake: a light institute
      brand makes white ink unreadable. It is `--primary-solid` /
      `--primary-solid-fg` now, the second of which is picked by measured contrast.
   🔴 Four hardcoded status colours — `#16a34a` paid, `#dc2626` delete and error,
      `#d97706` warn — plus `rgba(217,119,6,.1)` and `rgba(220,38,38,.08)` tints,
      all light-only. They are the --success / --danger / --warning families now,
      which invert on their own.
   ⚠️ Eleven dead fallbacks: `var(--border-color, var(--border-primary))`,
      `var(--input-bg, var(--bg-primary))`, `var(--text-tertiary, var(--text-secondary))`.
      Every one of those tokens exists, so no fallback ever fired.
   ⚠️ RADII, written down BEFORE converting because this config remaps the scale:
      10px = tw-rounded-md · 8px = tw-rounded-sm · 9px and 7px have no step and are
      arbitrary values. `tw-rounded-lg` here would be 16px, not the 8px that eight
      years of muscle memory says. */

// The row grid, shared by the header and every installment row, so it is named
// once rather than repeated. 36px is the delete button's column. Below 560px it
// drops to two columns and the header hides — the sheet's own breakpoint, which
// is NOT Tailwind's `sm` (640px).
const ROW = "tw-grid tw-grid-cols-[1.4fr_1fr_1.1fr_36px] tw-gap-2 tw-items-center max-[560px]:tw-grid-cols-[1fr_1fr] max-[560px]:tw-auto-rows-auto";

// Three identical row inputs.
const CELL = "tw-bg-[var(--input-bg)] tw-border tw-border-[var(--input-border)] tw-text-content tw-rounded-[7px] tw-py-[7px] tw-px-[9px] tw-text-[0.85rem] tw-w-full";

// The uppercase micro-label, used by the summary and the row header.
const LABEL = "tw-text-[0.72rem] tw-text-content-subtle tw-uppercase tw-tracking-[0.03em]";

// A stacked label + input control in the plan-type strip.
const CTL = "tw-flex tw-flex-col tw-gap-[3px] tw-text-xs tw-text-content-muted";
const CTL_INPUT = "tw-bg-[var(--input-bg)] tw-border tw-border-[var(--input-border)] tw-text-content tw-rounded-[7px] tw-py-1.5 tw-px-[9px] tw-text-[0.85rem] tw-w-[140px]";

const FeePlanModal = ({ show, onHide, courseAssignment, existingPlan, onSaved }) => {
  const total = Math.round(Number(courseAssignment?.totalFees) || 0);
  const paid = Math.round(Number(courseAssignment?.paidFees) || 0);

  const [planType, setPlanType] = useState("emi");
  const [count, setCount] = useState(3);
  const [startDate, setStartDate] = useState(toISODate(new Date()));
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  // Seed state whenever the modal opens (or its target changes).
  useEffect(() => {
    if (!show) return;
    setErr("");
    if (existingPlan?.installments?.length) {
      setPlanType(existingPlan.planType || "custom");
      setCount(existingPlan.installments.length);
      setStartDate(toISODate(existingPlan.installments[0]?.dueDate) || toISODate(new Date()));
      setRows(
        existingPlan.installments.map((i) => ({
          label: i.label || "",
          amount: Math.round(Number(i.amount) || 0),
          dueDate: toISODate(i.dueDate),
        }))
      );
    } else {
      setPlanType("emi");
      setCount(3);
      const today = toISODate(new Date());
      setStartDate(today);
      setRows(buildEmiRows(total, 3, today));
    }
  }, [show, existingPlan, total]);

  const regenerate = (nextType, nextCount, nextStart) => {
    if (nextType === "onetime") {
      setRows([{ label: "Full Payment", amount: total, dueDate: nextStart }]);
    } else if (nextType === "emi") {
      setRows(buildEmiRows(total, Math.max(1, Number(nextCount) || 1), nextStart));
    }
    // custom: leave rows as-is (user manages them)
  };

  const handleTypeChange = (t) => {
    setPlanType(t);
    if (t === "onetime") { setCount(1); regenerate("onetime", 1, startDate); }
    else if (t === "emi") { regenerate("emi", count, startDate); }
  };

  const updateRow = (idx, field, value) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
    if (planType !== "custom") setPlanType("custom"); // manual edit → custom
  };
  const addRow = () => {
    setRows((prev) => [...prev, { label: `Installment ${prev.length + 1}`, amount: 0, dueDate: toISODate(addMonths(startDate, prev.length)) }]);
    setPlanType("custom");
  };
  const removeRow = (idx) => {
    setRows((prev) => prev.filter((_, i) => i !== idx));
    setPlanType("custom");
  };

  const sum = useMemo(() => rows.reduce((s, r) => s + Math.round(Number(r.amount) || 0), 0), [rows]);
  const mismatch = Math.abs(sum - total) > 1;

  const handleSave = async () => {
    setErr("");
    if (!courseAssignment?._id) { setErr("No enrollment selected."); return; }
    if (total <= 0) { setErr("Set the course fee before creating a plan."); return; }
    if (rows.length === 0) { setErr("Add at least one installment."); return; }
    if (rows.some((r) => !r.dueDate)) { setErr("Every installment needs a due date."); return; }
    if (mismatch) { setErr(`Installments total ${rupees(sum)} but the course fee is ${rupees(total)}. They must match.`); return; }

    setSaving(true);
    try {
      const { data } = await apiClient.post("/fees/plans", {
        courseAssignmentId: courseAssignment._id,
        planType,
        installments: rows.map((r, i) => ({
          label: r.label || `Installment ${i + 1}`,
          amount: Math.round(Number(r.amount) || 0),
          dueDate: r.dueDate,
        })),
      });
      onSaved?.(data.plan);
      onHide?.();
    } catch (e) {
      setErr(e?.response?.data?.message || "Failed to save the plan.");
    }
    setSaving(false);
  };

  const courseName = courseAssignment?.courseId?.name || courseAssignment?.courseName || "this course";

  return (
    <AppModal
      show={show}
      onHide={saving ? undefined : onHide}
      title="Installment Plan"
      icon={<Layers size={18} />}
      size="lg"
      footer={
        <>
          <AppButton variant="outline" type="button" onClick={onHide} disabled={saving}>Cancel</AppButton>
          <AppButton type="button" onClick={handleSave} disabled={saving || mismatch || rows.length === 0}>
            {saving ? "Saving…" : existingPlan ? "Update Plan" : "Create Plan"}
          </AppButton>
        </>
      }
    >
      <div className="tw-flex tw-flex-col tw-gap-4">
        <div className="tw-flex tw-flex-wrap tw-gap-5 tw-py-3 tw-px-4 tw-bg-surface-alt tw-border tw-border-line tw-rounded-md [&>div]:tw-flex [&>div]:tw-flex-col [&>div]:tw-gap-0.5 [&_strong]:tw-text-[0.95rem] [&_strong]:tw-text-content">
          <div><span className={LABEL}>Course</span><strong>{courseName}</strong></div>
          <div><span className={LABEL}>Total Fee</span><strong>{rupees(total)}</strong></div>
          {/* The arbitrary variant above sets every <strong> to --text-primary, so
              this one has to out-rank it rather than sit beside it at equal
              specificity — the same coin-flip FilePreview's two hover colours hit. */}
          <div><span className={LABEL}>Already Paid</span><strong className="!tw-text-success-fg">{rupees(paid)}</strong></div>
        </div>

        {/* Plan-type controls */}
        <div className="tw-flex tw-flex-wrap tw-items-end tw-gap-3">
          <div className="tw-inline-flex tw-p-[3px] tw-bg-surface-alt tw-border tw-border-line tw-rounded-[9px]">
            {[["onetime", "One-time"], ["emi", "EMI"], ["custom", "Custom"]].map(([v, label]) => (
              <button
                key={v}
                type="button"
                /* Active and inactive are COMPOSED, not layered. Two equal-specificity
                   colour rules on one element are decided by Tailwind's emitted source
                   order, not by the order they appear in the attribute — audit F16, in
                   miniature, and the same shape as FilterButton's active state. */
                className={`tw-border-0 tw-text-[0.82rem] tw-font-semibold tw-py-1.5 tw-px-3.5 tw-rounded-[7px] tw-cursor-pointer ${
                  planType === v
                    ? "tw-bg-primary-solid tw-text-primary-solid-fg"
                    : "tw-bg-transparent tw-text-content-muted"
                }`}
                onClick={() => handleTypeChange(v)}
              >{label}</button>
            ))}
          </div>
          {planType === "emi" && (
            <>
              <label className={CTL}>
                <span># of EMIs</span>
                <input className={CTL_INPUT} type="number" min={1} max={36} value={count}
                  onChange={(e) => { const c = Number(e.target.value); setCount(c); regenerate("emi", c, startDate); }} />
              </label>
              <label className={CTL}>
                <span>First due date</span>
                <input className={CTL_INPUT} type="date" value={startDate}
                  onChange={(e) => { setStartDate(e.target.value); regenerate("emi", count, e.target.value); }} />
              </label>
            </>
          )}
          {planType === "onetime" && (
            <label className={CTL}>
              <span>Due date</span>
              <input className={CTL_INPUT} type="date" value={startDate}
                onChange={(e) => { setStartDate(e.target.value); regenerate("onetime", 1, e.target.value); }} />
            </label>
          )}
        </div>

        {/* Installment rows */}
        <div className="tw-flex tw-flex-col tw-gap-1.5">
          <div className={ROW + " " + LABEL + " tw-px-1 max-[560px]:tw-hidden"}>
            <span>Installment</span><span>Amount (₹)</span><span>Due date</span><span />
          </div>
          {rows.map((r, idx) => (
            <div className={ROW} key={idx}>
              <input className={CELL} type="text" value={r.label}
                onChange={(e) => updateRow(idx, "label", e.target.value)} placeholder={`Installment ${idx + 1}`} />
              <input className={CELL} type="number" min={0} value={r.amount}
                onChange={(e) => updateRow(idx, "amount", e.target.value)} />
              <input className={CELL} type="date" value={r.dueDate}
                onChange={(e) => updateRow(idx, "dueDate", e.target.value)} />
              <button
                type="button"
                onClick={() => removeRow(idx)}
                disabled={rows.length <= 1}
                title="Remove"
                className="tw-inline-flex tw-items-center tw-justify-center tw-border tw-border-line tw-bg-transparent tw-text-danger tw-rounded-[7px] tw-h-[34px] tw-cursor-pointer disabled:tw-opacity-40 disabled:tw-cursor-not-allowed"
              >
                <Trash2 size={15} />
              </button>
            </div>
          ))}
          <AppButton variant="outline" size="sm" type="button" onClick={addRow} className="tw-self-start tw-mt-0.5">
            <Plus size={14} /> Add installment
          </AppButton>
        </div>

        {/* Live total check */}
        <div
          className={`tw-flex tw-items-center tw-gap-2 tw-text-[0.85rem] tw-text-content-muted tw-py-2 tw-px-3 tw-rounded-sm [&_strong]:tw-text-content ${
            mismatch ? "tw-bg-warning-soft" : "tw-bg-surface-alt"
          }`}
        >
          <CalendarClock size={15} />
          <span>Installments total</span>
          <strong>{rupees(sum)}</strong>
          <span className="tw-text-content-subtle">of {rupees(total)}</span>
          {mismatch && <span className="tw-text-warning-fg tw-font-semibold tw-ml-auto">must match course fee</span>}
        </div>

        {err && (
          <div className="tw-flex tw-items-center tw-gap-1.5 tw-text-danger-fg tw-text-[0.82rem] tw-bg-danger-soft tw-py-2 tw-px-3 tw-rounded-sm">
            <AlertCircle size={14} /> {err}
          </div>
        )}
      </div>
    </AppModal>
  );
};

export default FeePlanModal;
